
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import {
  Flex,
  FlexItem,
  FlexAlign,
  FlexDirection,
  FlexJustify,
  FlexSpacer,
  Size,
  Margin,
  Title,
  Direction,
  Speed,
} from '@drawbotics/react-drylus';
import Playground, { PropsTable } from '~/components/Playground';
export const Component = () => {
  return <div style={{
    height: '50px',
    width: '100px',
    background: 'red',
    border: '1px solid orange'
  }} />;
};
export const FullWidthItem = ({
  background
}) => {
  return <div style={{
    height: '50px',
    width: '100%',
    background,
    padding: '15px'
  }}>
      {background}
    </div>;
};
export const Item = ({
  background
}) => {
  return <div style={{
    height: '50px',
    width: '100px',
    background,
    padding: '15px'
  }}>
      {background}
    </div>;
};

const layoutProps = {
  Component,
FullWidthItem,
Item
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


<h1>{`Flex`}</h1>
<p>{`For more in depth examples about how flexbox works, please refer to `}<a parentName="p" {...{
        "href": "https://css-tricks.com/snippets/css/a-guide-to-flexbox/"
      }}>{`https://css-tricks.com/snippets/css/a-guide-to-flexbox/`}</a>{`. If you need to create complex layouts, refer to [] grid.`}</p>
<pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import { Flex, FlexItem, FlexAlign, FlexJustify, FlexDirection } from '@drawbotics/react-drylus';
`}</code></pre>
<h3>{`The Flex container`}</h3>
<p>{`Here is an interactive example of the `}<inlineCode parentName="p">{`Flex`}</inlineCode>{` container. It can only take `}<inlineCode parentName="p">{`FlexItem`}</inlineCode>{` as children. Below you can see some fixed size content within each item.`}</p>

<Playground __position={0} __code={`<div style={{ border: '2px solid black' }}>
  <Flex>
    <FlexItem>
      <Component />
    </FlexItem>
    <FlexItem>
      <Component />
    </FlexItem>
    <FlexItem>
      <Component />
    </FlexItem>
    <FlexItem>
      <Component />
    </FlexItem>
    <FlexItem>
      <Component />
    </FlexItem>
    <FlexItem>
      <Component />
    </FlexItem>
  </Flex>
</div>`} component={Flex} enums={{
      FlexAlign,
      FlexDirection,
      FlexJustify
    }} mdxType="Playground">
  <div style={{
        border: '2px solid black'
      }}>
    <Flex mdxType="Flex">
      <FlexItem mdxType="FlexItem">
        <Component mdxType="Component" />
      </FlexItem>
      <FlexItem mdxType="FlexItem">
        <Component mdxType="Component" />
      </FlexItem>
      <FlexItem mdxType="FlexItem">
        <Component mdxType="Component" />
      </FlexItem>
      <FlexItem mdxType="FlexItem">
        <Component mdxType="Component" />
      </FlexItem>
      <FlexItem mdxType="FlexItem">
        <Component mdxType="Component" />
      </FlexItem>
      <FlexItem mdxType="FlexItem">
        <Component mdxType="Component" />
      </FlexItem>
    </Flex>
  </div>
</Playground>
<h3>{`The Flex Item`}</h3>
<p>{`The `}<inlineCode parentName="p">{`FlexItem`}</inlineCode>{` takes the following props`}</p>
<PropsTable component={FlexItem} mdxType="PropsTable" />
<p>{`The `}<inlineCode parentName="p">{`flex`}</inlineCode>{` property can take values of many types. Here is the list and what they do:`}</p>
<ul>
<li parentName="ul">{`true: passing `}<inlineCode parentName="li">{`true`}</inlineCode>{` (or simply the `}<inlineCode parentName="li">{`flex`}</inlineCode>{` prop without value) will set the `}<inlineCode parentName="li">{`flex`}</inlineCode>{` property to 1. If this prop is passed to all the `}<inlineCode parentName="li">{`FlexItem`}</inlineCode>{`s they will all take the same space.`}</li>
<li parentName="ul">{`Number < 1: for example `}<inlineCode parentName="li">{`1/4`}</inlineCode>{` or `}<inlineCode parentName="li">{`3/5`}</inlineCode>{`. When a value below 1 is passed, the component will interpret it as the `}<inlineCode parentName="li">{`flex-basis`}</inlineCode>{` and will set `}<inlineCode parentName="li">{`flex-grow`}</inlineCode>{` and `}<inlineCode parentName="li">{`flex-shrink`}</inlineCode>{` to 0. So `}<inlineCode parentName="li">{`flex={1/4}`}</inlineCode>{` equals `}<inlineCode parentName="li">{`flex: 0 0 25%`}</inlineCode>{` This is useful for building grid layouts (See example below).`}</li>
<li parentName="ul">{`Number > 1: this will be interpreted as the `}<inlineCode parentName="li">{`flex`}</inlineCode>{` value. E.g. `}<inlineCode parentName="li">{`flex={3}`}</inlineCode>{` equals `}<inlineCode parentName="li">{`flex: 3`}</inlineCode>{`.`}</li>
</ul>
<p>{`Here is an example with items that share the whole amount of the flex container. This is achieved by passing `}<inlineCode parentName="p">{`span`}</inlineCode>{` as a boolean, translates to `}<inlineCode parentName="p">{`flex: 1`}</inlineCode>{` for any item with that property.`}</p>

<Playground __position={2} __code={`<Flex>
  <FlexItem flex>
    <FullWidthItem background="red" />
  </FlexItem>
  <FlexItem flex>
    <FullWidthItem background="green" />
  </FlexItem>
  <FlexItem flex>
    <FullWidthItem background="yellow" />
  </FlexItem>
</Flex>`} mdxType="Playground">
  <Flex mdxType="Flex">
    <FlexItem flex mdxType="FlexItem">
      <FullWidthItem background="red" mdxType="FullWidthItem" />
    </FlexItem>
    <FlexItem flex mdxType="FlexItem">
      <FullWidthItem background="green" mdxType="FullWidthItem" />
    </FlexItem>
    <FlexItem flex mdxType="FlexItem">
      <FullWidthItem background="yellow" mdxType="FullWidthItem" />
    </FlexItem>
  </Flex>
</Playground>
<p>{`And here is an example where the `}<inlineCode parentName="p">{`FlexItem`}</inlineCode>{`s take only the space they determine (no `}<inlineCode parentName="p">{`flex`}</inlineCode>{` attribute on the `}<inlineCode parentName="p">{`FlexItem`}</inlineCode>{`). To wrap them pass `}<inlineCode parentName="p">{`wrap`}</inlineCode>{` to the `}<inlineCode parentName="p">{`Flex`}</inlineCode>{` container.`}</p>

<Playground __position={3} __code={`<div style={{ border: '2px solid black' }}>
  <Flex justify={FlexJustify.START} wrap>
    <FlexItem>
      <Item background="red" />
    </FlexItem>
    <FlexItem>
      <Item background="green" />
    </FlexItem>
    <FlexItem>
      <Item background="yellow" />
    </FlexItem>
    <FlexItem>
      <Item background="pink" />
    </FlexItem>
    <FlexItem>
      <Item background="blue" />
    </FlexItem>
  </Flex>
</div>
<div style={{ border: '2px solid black', marginTop: '20px', width: '240px' }}>
  <Flex justify={FlexJustify.START} wrap>
    <FlexItem>
      <Item background="red" />
    </FlexItem>
    <FlexItem>
      <Item background="green" />
    </FlexItem>
    <FlexItem>
      <Item background="yellow" />
    </FlexItem>
    <FlexItem>
      <Item background="pink" />
    </FlexItem>
    <FlexItem>
      <Item background="blue" />
    </FlexItem>
  </Flex>
</div>`} mdxType="Playground">
  <div style={{
        border: '2px solid black'
      }}>
    <Flex justify={FlexJustify.START} wrap mdxType="Flex">
      <FlexItem mdxType="FlexItem">
        <Item background="red" mdxType="Item" />
      </FlexItem>
      <FlexItem mdxType="FlexItem">
        <Item background="green" mdxType="Item" />
      </FlexItem>
      <FlexItem mdxType="FlexItem">
        <Item background="yellow" mdxType="Item" />
      </FlexItem>
      <FlexItem mdxType="FlexItem">
        <Item background="pink" mdxType="Item" />
      </FlexItem>
      <FlexItem mdxType="FlexItem">
        <Item background="blue" mdxType="Item" />
      </FlexItem>
    </Flex>
  </div>
  <div style={{
        border: '2px solid black',
        marginTop: '20px',
        width: '240px'
      }}>
    <Flex justify={FlexJustify.START} wrap mdxType="Flex">
      <FlexItem mdxType="FlexItem">
        <Item background="red" mdxType="Item" />
      </FlexItem>
      <FlexItem mdxType="FlexItem">
        <Item background="green" mdxType="Item" />
      </FlexItem>
      <FlexItem mdxType="FlexItem">
        <Item background="yellow" mdxType="Item" />
      </FlexItem>
      <FlexItem mdxType="FlexItem">
        <Item background="pink" mdxType="Item" />
      </FlexItem>
      <FlexItem mdxType="FlexItem">
        <Item background="blue" mdxType="Item" />
      </FlexItem>
    </Flex>
  </div>
</Playground>
<h3>{`Flex Spacer`}</h3>
<p>{`You can use a `}<inlineCode parentName="p">{`FlexSpacer`}</inlineCode>{` component to add margin between your `}<inlineCode parentName="p">{`FlexItems`}</inlineCode>{`:`}</p>
<PropsTable component={FlexSpacer} mdxType="PropsTable" />
<p>{`Example usage:`}</p>
<Playground __position={5} __code={`<>
  <Margin size={{ bottom: Size.EXTRA_LARGE }}>
    <Flex>
      <FlexItem>
        <Item background="red" />
      </FlexItem>
      <FlexSpacer size={Size.LARGE} />
      <FlexItem>
        <Item background="red" />
      </FlexItem>
    </Flex>
  </Margin>
  <Flex direction={FlexDirection.VERTICAL}>
    <FlexItem>
      <Item background="red" />
    </FlexItem>
    <FlexSpacer size={Size.SMALL} />
    <FlexItem>
      <Item background="red" />
    </FlexItem>
  </Flex>
</>`} mdxType="Playground">
  <>
    <Margin size={{
          bottom: Size.EXTRA_LARGE
        }} mdxType="Margin">
      <Flex mdxType="Flex">
        <FlexItem mdxType="FlexItem">
          <Item background="red" mdxType="Item" />
        </FlexItem>
        <FlexSpacer size={Size.LARGE} mdxType="FlexSpacer" />
        <FlexItem mdxType="FlexItem">
          <Item background="red" mdxType="Item" />
        </FlexItem>
      </Flex>
    </Margin>
    <Flex direction={FlexDirection.VERTICAL} mdxType="Flex">
      <FlexItem mdxType="FlexItem">
        <Item background="red" mdxType="Item" />
      </FlexItem>
      <FlexSpacer size={Size.SMALL} mdxType="FlexSpacer" />
      <FlexItem mdxType="FlexItem">
        <Item background="red" mdxType="Item" />
      </FlexItem>
    </Flex>
  </>
</Playground>
<h2>{`Animated Flex example`}</h2>
<p>{`By passing the `}<inlineCode parentName="p">{`animated`}</inlineCode>{` prop to the `}<inlineCode parentName="p">{`Flex`}</inlineCode>{` container, the component will automatically animate its children, with each child bearing a small delay compared to the previous one (stagger effect). You can modify the animation options through the following props:`}</p>
<ul>
<li parentName="ul"><inlineCode parentName="li">{`animationDirection`}</inlineCode>{`: where the items should be coming in from (this is limited to each child, so `}<inlineCode parentName="li">{`BOTTOM_UP`}</inlineCode>{` will mean that each item will have a small transition from a couple of pixels below its final position)`}</li>
<li parentName="ul"><inlineCode parentName="li">{`animationSpeed`}</inlineCode>{`: how fast items are coming in`}</li>
<li parentName="ul"><inlineCode parentName="li">{`animationEase`}</inlineCode></li>
</ul>
<h4>{`No direction, Scale`}</h4>
<p>{`Note: the animation will transform the FlexItem, so if your content is only aligned to the left of your FlexItem an unwanted visual effect my appear. If that's the case, you can use `}<inlineCode parentName="p">{`FlexSpacer`}</inlineCode>{` with `}<inlineCode parentName="p">{`flex`}</inlineCode>{` to achieve the same effect (see below).`}</p>
<Playground __position={6} __code={`<Flex animated>
  <FlexItem>
    <Item background="red" />
  </FlexItem>
  <FlexSpacer size={Size.SMALL} flex />
  <FlexItem>
    <Item background="blue" />
  </FlexItem>
  <FlexSpacer size={Size.SMALL} />
  <FlexItem>
    <Item background="blue" />
  </FlexItem>
  <FlexSpacer size={Size.SMALL} />
  <FlexItem>
    <Item background="blue" />
  </FlexItem>
</Flex>`} mode="jsx" mdxType="Playground">
  <Flex animated mdxType="Flex">
    <FlexItem mdxType="FlexItem">
      <Item background="red" mdxType="Item" />
    </FlexItem>
    <FlexSpacer size={Size.SMALL} flex mdxType="FlexSpacer" />
    <FlexItem mdxType="FlexItem">
      <Item background="blue" mdxType="Item" />
    </FlexItem>
    <FlexSpacer size={Size.SMALL} mdxType="FlexSpacer" />
    <FlexItem mdxType="FlexItem">
      <Item background="blue" mdxType="Item" />
    </FlexItem>
    <FlexSpacer size={Size.SMALL} mdxType="FlexSpacer" />
    <FlexItem mdxType="FlexItem">
      <Item background="blue" mdxType="Item" />
    </FlexItem>
  </Flex>
</Playground>
<h4>{`Top down, slow`}</h4>
<Playground __position={7} __code={`<Flex animated animationDirection={Direction.TOP_DOWN} animationSpeed={Speed.SLOW}>
  <FlexItem flex>
    <Item background="red" />
  </FlexItem>
  <FlexItem>
    <Item background="blue" />
  </FlexItem>
  <FlexSpacer size={Size.SMALL} />
  <FlexItem>
    <Item background="blue" />
  </FlexItem>
  <FlexSpacer size={Size.SMALL} />
  <FlexItem>
    <Item background="blue" />
  </FlexItem>
</Flex>`} mode="jsx" mdxType="Playground">
  <Flex animated animationDirection={Direction.TOP_DOWN} animationSpeed={Speed.SLOW} mdxType="Flex">
    <FlexItem flex mdxType="FlexItem">
      <Item background="red" mdxType="Item" />
    </FlexItem>
    <FlexItem mdxType="FlexItem">
      <Item background="blue" mdxType="Item" />
    </FlexItem>
    <FlexSpacer size={Size.SMALL} mdxType="FlexSpacer" />
    <FlexItem mdxType="FlexItem">
      <Item background="blue" mdxType="Item" />
    </FlexItem>
    <FlexSpacer size={Size.SMALL} mdxType="FlexSpacer" />
    <FlexItem mdxType="FlexItem">
      <Item background="blue" mdxType="Item" />
    </FlexItem>
  </Flex>
</Playground>
<h4>{`Left right, fast`}</h4>
<Playground __position={8} __code={`<Flex direction={FlexDirection.VERTICAL} animated animationDirection={Direction.LEFT_RIGHT} animationSpeed={Speed.FAST}>
  <FlexItem>
    <Item background="red" />
  </FlexItem>
  <FlexSpacer size={Size.SMALL} />
  <FlexItem>
    <Item background="blue" />
  </FlexItem>
  <FlexSpacer size={Size.SMALL} />
  <FlexItem>
    <Item background="blue" />
  </FlexItem>
  <FlexSpacer size={Size.SMALL} />
  <FlexItem>
    <Item background="blue" />
  </FlexItem>
</Flex>`} mode="jsx" mdxType="Playground">
  <Flex direction={FlexDirection.VERTICAL} animated animationDirection={Direction.LEFT_RIGHT} animationSpeed={Speed.FAST} mdxType="Flex">
    <FlexItem mdxType="FlexItem">
      <Item background="red" mdxType="Item" />
    </FlexItem>
    <FlexSpacer size={Size.SMALL} mdxType="FlexSpacer" />
    <FlexItem mdxType="FlexItem">
      <Item background="blue" mdxType="Item" />
    </FlexItem>
    <FlexSpacer size={Size.SMALL} mdxType="FlexSpacer" />
    <FlexItem mdxType="FlexItem">
      <Item background="blue" mdxType="Item" />
    </FlexItem>
    <FlexSpacer size={Size.SMALL} mdxType="FlexSpacer" />
    <FlexItem mdxType="FlexItem">
      <Item background="blue" mdxType="Item" />
    </FlexItem>
  </Flex>
</Playground>
<h2>{`Layout examples`}</h2>
<p>{`A responsive grid layout with 4 columns, aligned to the left. Becomes a 2 column layout when the screen is "medium". Use `}<a parentName="p" {...{
        "href": "/component-kit/components/grid"
      }}><inlineCode parentName="a">{`Grid`}</inlineCode></a>{` for this instead.`}</p>
<Playground __position={9} __code={`<div style={{ border: '2px solid black' }}>
  <Flex justify={FlexJustify.START} wrap>
    <FlexItem flex={1/4}>
      <FullWidthItem background="red" />
    </FlexItem>
    <FlexItem flex={1/4}>
      <FullWidthItem background="green" />
    </FlexItem>
    <FlexItem flex={1/4}>
      <FullWidthItem background="yellow" />
    </FlexItem>
    <FlexItem flex={1/4}>
      <FullWidthItem background="pink" />
    </FlexItem>
    <FlexItem flex={1/4}>
      <FullWidthItem background="blue" />
    </FlexItem>
  </Flex>
</div>`} mdxType="Playground">
  <div style={{
        border: '2px solid black'
      }}>
    <Flex justify={FlexJustify.START} wrap mdxType="Flex">
      <FlexItem flex={1 / 4} mdxType="FlexItem">
        <FullWidthItem background="red" mdxType="FullWidthItem" />
      </FlexItem>
      <FlexItem flex={1 / 4} mdxType="FlexItem">
        <FullWidthItem background="green" mdxType="FullWidthItem" />
      </FlexItem>
      <FlexItem flex={1 / 4} mdxType="FlexItem">
        <FullWidthItem background="yellow" mdxType="FullWidthItem" />
      </FlexItem>
      <FlexItem flex={1 / 4} mdxType="FlexItem">
        <FullWidthItem background="pink" mdxType="FullWidthItem" />
      </FlexItem>
      <FlexItem flex={1 / 4} mdxType="FlexItem">
        <FullWidthItem background="blue" mdxType="FullWidthItem" />
      </FlexItem>
    </Flex>
  </div>
</Playground>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;